import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Table, Form, Button, Badge, Alert } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useQuery, useMutation } from '@apollo/client';
import { GET_ADMIN_USERS, GET_INVOICES, CREATE_INVOICE, DELETE_INVOICE, UPDATE_INVOICE, UPDATE_ORDER_STATUS } from '../../graphql/operations';
import CustomerModal from './CustomerModal';

interface ProductOptionChoice {
  label: string;
  extraCost: number;
}

interface ProductOption {
  name: string;
  label: string;
  type: string;
  choices?: ProductOptionChoice[];
  min?: number | null;
  max?: number | null;
  step?: number | null;
  unit?: string | null;
}

interface Product {
  id: string;
  productId: string;
  name: string;
  basePrice: number;
  description?: string;
  category?: string;
  options?: ProductOption[];
  imageUrl?: string;
}

interface InvoiceItem {
  id: string;
  title: string;
  description: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  originalUnitPrice: number;
  isOverridden: boolean;
  total: number;
  isCustom?: boolean;
  hsnCode?: string;
}

export interface DetailedHsnItem {
  code: string;
  label: string;
  description: string;
  gstRate: number;
}

export interface DetailedHsnCategory {
  category: string;
  icon: string;
  items: DetailedHsnItem[];
}

export const DETAILED_HSN_CATEGORIES: DetailedHsnCategory[] = [
  {
    category: 'Large Format, Flex & Signage (Chapter 49 & 39)',
    icon: 'fa-image',
    items: [
      { code: '4911 10 10', label: '4911 10 10 - Flex Banners, Hoardings & Advertising Posters', description: 'Commercial outdoor banners, flex hoardings, vinyl signboards', gstRate: 18 },
      { code: '4911 10 90', label: '4911 10 90 - Other Advertising Posters & Displays', description: 'Promotional posters, standees, exhibition backdrops', gstRate: 18 },
      { code: '4911 91 00', label: '4911 91 00 - Art Canvas, Photo & Digital Picture Prints', description: 'Fine art prints, mounted canvas, framing photo prints', gstRate: 12 },
      { code: '4911 99 90', label: '4911 99 90 - Other Printed Matter (Signage & General)', description: 'Display boards, acrylic lettering, UV flatbed prints', gstRate: 18 },
      { code: '3919 90 90', label: '3919 90 90 - Self-Adhesive Vinyl Prints & Cut Decals', description: 'Vinyl vehicle wraps, wall graphics, glass frosted decals', gstRate: 18 },
      { code: '3926 90 99', label: '3926 90 99 - Acrylic & Foam Sheet Displays / Standees', description: 'Plastic display frames, acrylic LED glow signs, 3D lettering', gstRate: 18 },
    ],
  },
  {
    category: 'Commercial & Marketing Materials (Chapter 49)',
    icon: 'fa-bullhorn',
    items: [
      { code: '4901 10 10', label: '4901 10 10 - Single Sheet Flyers, Leaflets & Pamphlets', description: 'Handouts, product inserts, marketing flyers, leaflets', gstRate: 5 },
      { code: '4901 99 00', label: '4901 99 00 - Folded Brochures, Booklets & Product Manuals', description: 'Bi-fold/tri-fold brochures, multi-page company profiles', gstRate: 12 },
      { code: '4911 10 20', label: '4911 10 20 - Commercial Catalogues & Price Lists', description: 'Multi-page trade catalogues, product range booklets', gstRate: 18 },
      { code: '4910 00 00', label: '4910 00 00 - Calendars (Wall, Desk & Pocket Blocks)', description: 'Custom printed corporate desk and wall calendars', gstRate: 12 },
    ],
  },
  {
    category: 'Cards, Invitations & Stationery (Chapter 49 & 48)',
    icon: 'fa-id-card',
    items: [
      { code: '4909 00 90', label: '4909 00 90 - Visiting Cards, Business & ID Cards', description: 'Matte/gloss laminated business cards, PVC ID cards', gstRate: 18 },
      { code: '4909 00 10', label: '4909 00 10 - Wedding Invitations & Greeting Cards', description: 'Personal invitations, festive greeting cards, bespoke stationery', gstRate: 18 },
      { code: '4817 10 00', label: '4817 10 00 - Printed Office Envelopes & Letterheads', description: 'Corporate branded envelopes, letterhead sheets', gstRate: 18 },
      { code: '4820 10 00', label: '4820 10 00 - Bill Books, Receipt Books, Note Pads & Vouchers', description: 'Carbonless receipt books, duplicate invoice pads, memo pads', gstRate: 18 },
      { code: '4820 20 00', label: '4820 20 00 - Spiral Notebooks & Custom Diaries', description: 'Wire-o bound notebooks, personalized executive diaries', gstRate: 12 },
      { code: '9611 00 00', label: '9611 00 00 - Rubber Stamps & Self-Inking Seals', description: 'Pre-inked stamps, dater stamps, round corporate seals', gstRate: 18 },
    ],
  },
  {
    category: 'Labels, Stickers & Packaging (Chapter 48 & 39)',
    icon: 'fa-tags',
    items: [
      { code: '4821 10 10', label: '4821 10 10 - Paper Labels & Multi-Color Stickers', description: 'Glossy paper stickers, die-cut packaging labels', gstRate: 18 },
      { code: '4821 10 20', label: '4821 10 20 - Barcode & QR Code Adhesive Labels', description: 'Thermal labels, inventory tags, serialized barcode labels', gstRate: 18 },
      { code: '3919 10 00', label: '3919 10 00 - Waterproof Vinyl & Transparent Stickers', description: 'Waterproof bumper stickers, transparent bottle labels', gstRate: 18 },
      { code: '4819 10 10', label: '4819 10 10 - Corrugated Packaging Boxes & Cartons', description: 'Printed shipping boxes, custom mailer cartons', gstRate: 18 },
      { code: '4819 20 20', label: '4819 20 20 - Rigid Sweet & Retail Folding Boxes', description: 'Laminated luxury gift boxes, non-corrugated cartons', gstRate: 18 },
      { code: '4819 40 00', label: '4819 40 00 - Printed Paper Bags & Carry Bags', description: 'Kraft paper bags, boutique shopping bags with rope handles', gstRate: 18 },
    ],
  },
  {
    category: 'Printing & Finishing Jobwork Services (SAC 9989)',
    icon: 'fa-cogs',
    items: [
      { code: '9989 19', label: '9989 19 - Digital, Flex & Commercial Printing Jobwork', description: 'Jobwork printing services on client-supplied or custom media', gstRate: 18 },
      { code: '9989 12', label: '9989 12 - Books, Brochures & Documents Printing Jobwork', description: 'Printing and reproduction services for books and publications', gstRate: 12 },
      { code: '9989 22', label: '9989 22 - Lamination, Binding & Finishing Jobwork', description: 'Thermal lamination, perfect hard binding, creasing, die-cutting', gstRate: 18 },
      { code: '9989 21', label: '9989 21 - Plate Making & Pre-Press Imaging Services', description: 'Offset CTP plates, positive film outputs, screen exposures', gstRate: 18 },
    ],
  },
  {
    category: 'Design & Pre-Press Technical Services (SAC 9983)',
    icon: 'fa-paint-brush',
    items: [
      { code: '9983 13', label: '9983 13 - Graphic Designing, Logo & DTP Composition', description: 'Creative artwork preparation, brochure layouts, branding design', gstRate: 18 },
      { code: '9983 99', label: '9983 99 - Other Pre-Press Technical & Consulting Services', description: 'Color correction, vector trace, typography composition', gstRate: 18 },
    ],
  },
];

export const ALL_DETAILED_HSN_CODES: DetailedHsnItem[] = DETAILED_HSN_CATEGORIES.flatMap(cat => cat.items);
export const STANDARD_HSN_CODES = ALL_DETAILED_HSN_CODES;

export const getDefaultHsnCode = (productName: string = '', category: string = ''): string => {
  const text = `${productName} ${category}`.toLowerCase();
  if (text.includes('visiting') || text.includes('business card') || text.includes('id card')) {
    return '4909 00 90';
  }
  if (text.includes('wedding') || text.includes('invite') || text.includes('invitation') || text.includes('greeting')) {
    return '4909 00 10';
  }
  if (text.includes('flex') || text.includes('banner') || text.includes('hoarding')) {
    return '4911 10 10';
  }
  if (text.includes('poster') || text.includes('standee') || text.includes('signage')) {
    return '4911 10 90';
  }
  if (text.includes('canvas') || text.includes('artwork') || text.includes('photo print')) {
    return '4911 91 00';
  }
  if (text.includes('flyer') || text.includes('leaflet') || text.includes('pamphlet') || text.includes('handout')) {
    return '4901 10 10';
  }
  if (text.includes('brochure') || text.includes('booklet') || text.includes('profile')) {
    return '4901 99 00';
  }
  if (text.includes('catalog') || text.includes('catalogue')) {
    return '4911 10 20';
  }
  if (text.includes('calendar')) {
    return '4910 00 00';
  }
  if (text.includes('envelope') || text.includes('letterhead')) {
    return '4817 10 00';
  }
  if (text.includes('bill book') || text.includes('receipt') || text.includes('voucher') || text.includes('pad')) {
    return '4820 10 00';
  }
  if (text.includes('diary') || text.includes('notebook') || text.includes('spiral')) {
    return '4820 20 00';
  }
  if (text.includes('stamp') || text.includes('seal')) {
    return '9611 00 00';
  }
  if (text.includes('barcode') || text.includes('qr')) {
    return '4821 10 20';
  }
  if (text.includes('sticker') || text.includes('label')) {
    return '4821 10 10';
  }
  if (text.includes('vinyl') || text.includes('decal') || text.includes('frosted')) {
    return '3919 90 90';
  }
  if (text.includes('acrylic') || text.includes('foam board') || text.includes('glow sign')) {
    return '3926 90 99';
  }
  if (text.includes('bag') || text.includes('carry')) {
    return '4819 40 00';
  }
  if (text.includes('carton') || text.includes('box') || text.includes('packaging')) {
    return '4819 10 10';
  }
  if (text.includes('design') || text.includes('dtp') || text.includes('logo')) {
    return '9983 13';
  }
  if (text.includes('binding') || text.includes('lamination')) {
    return '9989 22';
  }
  return '4911 10 10';
};

interface SavedInvoice {
  invoiceNo: string;
  orderId?: string;
  orderStatus?: string;
  date: string;
  dueDate: string;
  preparedBy: string;
  paymentStatus: 'Paid' | 'Pending' | 'Partially Paid';
  paymentMode: 'UPI' | 'Cash' | 'Card' | 'Bank Transfer';
  clientInfo: {
    name: string;
    customerId?: string;
    company: string;
    phone: string;
    email: string;
    address: string;
    gstin: string;
  };
  items: InvoiceItem[];
  subtotal: number;
  discountType: 'percentage' | 'fixed';
  discountVal: number;
  discountAmount: number;
  taxRate: number;
  taxAmount: number;
  shipping: number;
  grandTotal: number;
  savedAt: string;
}

interface InvoiceGeneratorProps {
  products: Product[];
  users: any[];
}

// Convert numbers to words (INR format)
const numberToWords = (num: number): string => {
  const a = [
    '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
    'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'
  ];
  const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  const g = ['', 'Thousand', 'Lakh', 'Crore'];

  const limit = 1000000000;

  if (num === 0) return 'Zero Rupees Only';

  const formatTens = (n: number) => {
    if (n < 20) return a[n];
    return b[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + a[n % 10] : '');
  };

  const formatHundreds = (n: number) => {
    if (n > 99) {
      return a[Math.floor(n / 100)] + ' Hundred' + (n % 100 !== 0 ? ' and ' + formatTens(n % 100) : '');
    }
    return formatTens(n);
  };

  const convertSection = (n: number) => {
    if (n === 0) return '';
    return formatHundreds(n);
  };

  let wordRepresentation = '';
  const integerPart = Math.floor(num);
  const decimalPart = Math.round((num - integerPart) * 100);

  // Split into components (INR format: crore, lakh, thousand, remainder)
  const segments = [];
  segments.push(integerPart % 1000); // Remainder
  let temp = Math.floor(integerPart / 1000);
  segments.push(temp % 100); // Thousands
  temp = Math.floor(temp / 100);
  segments.push(temp % 100); // Lakhs
  temp = Math.floor(temp / 100);
  segments.push(temp % limit); // Crores

  for (let i = 0; i < segments.length; i++) {
    const val = segments[i];
    if (val > 0) {
      wordRepresentation = convertSection(val) + (g[i] ? ' ' + g[i] : '') + (wordRepresentation ? ' ' + wordRepresentation : '');
    }
  }

  let str = wordRepresentation;
  if (decimalPart > 0) {
    str += ` and ${formatTens(decimalPart)} Paise`;
  }
  return str.trim() + ' Rupees Only';
};

const InvoiceGenerator: React.FC<InvoiceGeneratorProps> = ({ products, users }) => {
  const [activeSubTab, setActiveSubTab] = useState<'create' | 'history'>('create');
  const [customerSearch, setCustomerSearch] = useState('');
  const [sortOption, setSortOption] = useState<'newest' | 'oldest' | 'highest' | 'lowest'>('newest');

  // Date-range calendar export
  const [dateRangeStart, setDateRangeStart] = useState<string>('');
  const [dateRangeEnd, setDateRangeEnd] = useState<string>('');
  const [showDatePicker, setShowDatePicker] = useState(false);

  const { data: invoicesData, refetch: refetchInvoices, loading: invoicesLoading } = useQuery(GET_INVOICES, { fetchPolicy: 'network-only' });
  const [createInvoice] = useMutation(CREATE_INVOICE);
  const [updateInvoice] = useMutation(UPDATE_INVOICE);
  const [deleteInvoice] = useMutation(DELETE_INVOICE);
  const [updateOrderStatusMutation] = useMutation(UPDATE_ORDER_STATUS);

  const handleUpdateOrderStatus = async (orderId: string, newStatus: string) => {
    try {
      const { data } = await updateOrderStatusMutation({
        variables: { orderId, status: newStatus }
      });
      if (data?.updateOrderStatus?.success) {
        refetchInvoices();
      } else {
        alert(data?.updateOrderStatus?.message || 'Failed to update order status');
      }
    } catch (err: any) {
      alert('Error updating order status: ' + err.message);
    }
  };

  const handleUpdateInvoicePaymentStatus = async (invoiceNo: string, newPaymentStatus: string) => {
    try {
      const { data } = await updateInvoice({
        variables: { invoiceNo, input: { paymentStatus: newPaymentStatus } }
      });
      if (data?.updateInvoice?.success) {
        refetchInvoices();
      } else {
        alert(data?.updateInvoice?.message || 'Failed to update payment status');
      }
    } catch (err: any) {
      alert('Error updating payment status: ' + err.message);
    }
  };

  const invoices: SavedInvoice[] = invoicesData?.invoices || [];

  const filteredAndSortedInvoices = React.useMemo(() => {
    let result = [...invoices];

    if (customerSearch.trim()) {
      const lowerSearch = customerSearch.toLowerCase();
      result = result.filter(inv =>
        inv.clientInfo.name.toLowerCase().includes(lowerSearch) ||
        (inv.clientInfo.company && inv.clientInfo.company.toLowerCase().includes(lowerSearch)) ||
        (inv.clientInfo.phone && inv.clientInfo.phone.includes(lowerSearch)) ||
        (inv.clientInfo.email && inv.clientInfo.email.toLowerCase().includes(lowerSearch)) ||
        inv.invoiceNo.toLowerCase().includes(lowerSearch)
      );
    }

    result.sort((a, b) => {
      const totalA = (a as any).totalAmount || a.grandTotal || 0;
      const totalB = (b as any).totalAmount || b.grandTotal || 0;
      if (sortOption === 'highest') {
        return totalB - totalA;
      } else if (sortOption === 'lowest') {
        return totalA - totalB;
      } else if (sortOption === 'oldest') {
        return new Date(a.date).getTime() - new Date(b.date).getTime();
      } else {
        return new Date(b.date).getTime() - new Date(a.date).getTime();
      }
    });

    return result;
  }, [invoices, customerSearch, sortOption]);

  const handleBulkExport = (timeframe: 'day' | 'week' | 'month' | 'year') => {
    const now = new Date();
    const targetInvoices = invoices.filter(inv => {
      if (!inv.date) return false;
      const invDate = new Date(inv.date);
      if (timeframe === 'day') {
        return invDate.toDateString() === now.toDateString();
      } else if (timeframe === 'week') {
        const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        return invDate >= oneWeekAgo && invDate <= now;
      } else if (timeframe === 'month') {
        return invDate.getMonth() === now.getMonth() && invDate.getFullYear() === now.getFullYear();
      } else if (timeframe === 'year') {
        return invDate.getFullYear() === now.getFullYear();
      }
      return true;
    });

    if (targetInvoices.length === 0) {
      alert(`No invoices found for the selected ${timeframe}.`);
      return;
    }

    const exportData = targetInvoices.map(inv => ({
      'Invoice No': inv.invoiceNo,
      'Date': inv.date,
      'Due Date': inv.dueDate,
      'Customer Name': inv.clientInfo.name,
      'Company': inv.clientInfo.company || '',
      'Phone': inv.clientInfo.phone || '',
      'Payment Status': inv.paymentStatus,
      'Payment Mode': inv.paymentMode,
      'Prepared By': inv.preparedBy,
      'Subtotal': inv.subtotal,
      'Discount': inv.discountAmount,
      'Tax': inv.taxAmount,
      'Shipping': inv.shipping,
      'Grand Total': (inv as any).totalAmount || inv.grandTotal || 0,
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, `Invoices_${timeframe}`);
    XLSX.writeFile(wb, `Invoices_Export_${timeframe}_${now.toISOString().split('T')[0]}.xlsx`);
  };

  // Date-range export handler
  const handleDateRangeExport = () => {
    if (!dateRangeStart || !dateRangeEnd) {
      alert('Please select both a start date and an end date.');
      return;
    }
    const start = new Date(dateRangeStart);
    const end = new Date(dateRangeEnd);
    end.setHours(23, 59, 59, 999); // include full end day

    if (start > end) {
      alert('Start date must be before or equal to the end date.');
      return;
    }

    const targetInvoices = invoices.filter(inv => {
      if (!inv.date) return false;
      const invDate = new Date(inv.date);
      return invDate >= start && invDate <= end;
    });

    if (targetInvoices.length === 0) {
      alert(`No invoices found between ${dateRangeStart} and ${dateRangeEnd}.`);
      return;
    }

    const exportData = targetInvoices.map(inv => ({
      'Invoice No': inv.invoiceNo,
      'Date': inv.date,
      'Due Date': inv.dueDate,
      'Customer Name': inv.clientInfo.name,
      'Company': inv.clientInfo.company || '',
      'Phone': inv.clientInfo.phone || '',
      'Payment Status': inv.paymentStatus,
      'Payment Mode': inv.paymentMode,
      'Prepared By': inv.preparedBy,
      'Subtotal': inv.subtotal,
      'Discount': inv.discountAmount,
      'Tax': inv.taxAmount,
      'Shipping': inv.shipping,
      'Grand Total': (inv as any).totalAmount || inv.grandTotal || 0,
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Invoices');
    XLSX.writeFile(wb, `Invoices_${dateRangeStart}_to_${dateRangeEnd}.xlsx`);
    setShowDatePicker(false);
  };

  // Date-range bulk PDF export — one full invoice page per invoice
  const handleDateRangePDFExport = () => {
    if (!dateRangeStart || !dateRangeEnd) {
      alert('Please select both a start date and an end date.');
      return;
    }
    const start = new Date(dateRangeStart);
    const end = new Date(dateRangeEnd);
    end.setHours(23, 59, 59, 999);

    if (start > end) {
      alert('Start date must be before or equal to the end date.');
      return;
    }

    const targetInvoices = invoices.filter(inv => {
      if (!inv.date) return false;
      const invDate = new Date(inv.date);
      return invDate >= start && invDate <= end;
    });

    if (targetInvoices.length === 0) {
      alert(`No invoices found between ${dateRangeStart} and ${dateRangeEnd}.`);
      return;
    }

    const doc = new jsPDF();

    targetInvoices.forEach((inv, pageIndex) => {
      if (pageIndex > 0) doc.addPage();

      // ── Header ──────────────────────────────────────────────
      doc.setFillColor(34, 43, 69);
      doc.rect(0, 0, 210, 40, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(22);
      doc.text('PRATHITHA DIGITAL PRINTERS', 15, 17);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.text('Premium Digital & Flex Printing Services | Eco-Solvent & Corporate Works', 15, 24);
      doc.text('GSTIN: 33CHVPP2054B1ZE | Ph: +91 91508 08130 | Chennai, Tamil Nadu', 15, 29);
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('TAX INVOICE', 150, 25);

      // ── Invoice metadata ────────────────────────────────────
      doc.setTextColor(51, 65, 85);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text('INVOICE METADATA:', 15, 50);
      doc.setFont('helvetica', 'normal');
      doc.text(`Invoice No: ${inv.invoiceNo}`, 15, 56);
      doc.text(`Invoice Date: ${inv.date}`, 15, 62);
      doc.text(`Due Date: ${inv.dueDate}`, 15, 68);
      doc.text(`Prepared By: ${inv.preparedBy}`, 15, 74);
      doc.setFont('helvetica', 'bold');
      doc.text(`Payment Mode: ${inv.paymentMode}`, 15, 82);

      // ── Bill To ─────────────────────────────────────────────
      doc.setFont('helvetica', 'bold');
      doc.text('BILL TO:', 120, 50);
      doc.setFont('helvetica', 'normal');
      doc.text(`Customer Name: ${inv.clientInfo.name}`, 120, 56);
      if (inv.clientInfo.customerId) doc.text(`Customer ID: ${inv.clientInfo.customerId}`, 120, 62);
      let yAddr = inv.clientInfo.customerId ? 68 : 62;
      if (inv.clientInfo.company) { doc.text(`Company: ${inv.clientInfo.company}`, 120, yAddr); yAddr += 6; }
      const contactStr = [inv.clientInfo.phone, inv.clientInfo.email].filter(Boolean).join(' | ');
      doc.text(contactStr, 120, yAddr); yAddr += 6;
      if (inv.clientInfo.address) { doc.text(`Address: ${inv.clientInfo.address}`, 120, yAddr); yAddr += 6; }
      if (inv.clientInfo.gstin) doc.text(`GSTIN: ${inv.clientInfo.gstin}`, 120, yAddr);

      // ── Payment status stamp ─────────────────────────────────
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      if (inv.paymentStatus === 'Paid') {
        doc.setFillColor(220, 252, 231); doc.rect(155, 33, 40, 8, 'F');
        doc.setTextColor(22, 101, 52); doc.text('PAID', 170, 39);
      } else if (inv.paymentStatus === 'Partially Paid') {
        doc.setFillColor(254, 243, 199); doc.rect(150, 33, 45, 8, 'F');
        doc.setTextColor(146, 64, 14); doc.text('PARTIALLY PAID', 153, 39);
      } else {
        doc.setFillColor(254, 226, 226); doc.rect(155, 33, 40, 8, 'F');
        doc.setTextColor(153, 27, 27); doc.text('PENDING', 165, 39);
      }

      doc.setTextColor(51, 65, 85);
      doc.setFontSize(9);
      doc.setDrawColor(226, 232, 240);
      doc.line(15, 90, 195, 90);

      // ── Line items table ─────────────────────────────────────
      const tableRows = (inv.items || []).map((item: any, idx: number) => [
        idx + 1,
        item.title ? `${item.title}\n${item.description || ''}` : (item.description || ''),
        item.hsnCode || '4911 10 10',
        item.quantity || 0,
        item.unit || '-',
        `INR ${(item.unitPrice || 0).toLocaleString('en-IN')}`,
        `INR ${(item.totalPrice || item.total || 0).toLocaleString('en-IN')}`,
      ]);

      autoTable(doc, {
        startY: 95,
        head: [['S.No', 'Product & Specs', 'HSN/SAC', 'Qty', 'Unit', 'Unit Price', 'Total']],
        body: tableRows,
        theme: 'striped',
        headStyles: { fillColor: [34, 43, 69], textColor: [255, 255, 255], fontStyle: 'bold' },
        columnStyles: {
          0: { cellWidth: 10 },
          1: { cellWidth: 70 },
          2: { cellWidth: 20, halign: 'center' },
          3: { cellWidth: 15, halign: 'center' },
          4: { cellWidth: 15, halign: 'center' },
          5: { cellWidth: 30, halign: 'right' },
          6: { cellWidth: 30, halign: 'right' },
        },
        styles: { fontSize: 8.5, cellPadding: 3.5, overflow: 'linebreak' },
      });

      // ── Financial summary ────────────────────────────────────
      let finalY = (doc as any).lastAutoTable.finalY + 10;
      if (finalY > 210) { doc.addPage(); finalY = 20; }

      doc.setFillColor(248, 250, 252);
      doc.rect(120, finalY, 75, 42, 'F');
      doc.setDrawColor(226, 232, 240);
      doc.rect(120, finalY, 75, 42, 'S');

      doc.setTextColor(100, 116, 139);
      doc.text('Subtotal:', 125, finalY + 7);
      doc.text(`Discount (${inv.discountType === 'percentage' ? inv.discountVal + '%' : 'Fixed'}):`, 125, finalY + 14);
      doc.text(`GST Tax (${inv.taxRate}%):`, 125, finalY + 21);
      doc.text('Delivery/Shipping:', 125, finalY + 28);
      doc.setTextColor(51, 65, 85);
      doc.text(`INR ${(inv.subtotal || 0).toLocaleString('en-IN')}`, 190, finalY + 7, { align: 'right' });
      doc.text(`- INR ${(inv.discountAmount || 0).toLocaleString('en-IN')}`, 190, finalY + 14, { align: 'right' });
      doc.text(`INR ${(inv.taxAmount || 0).toLocaleString('en-IN')}`, 190, finalY + 21, { align: 'right' });
      doc.text(`INR ${(inv.shipping || 0).toLocaleString('en-IN')}`, 190, finalY + 28, { align: 'right' });

      doc.line(122, finalY + 32, 193, finalY + 32);
      doc.setFont('helvetica', 'bold'); doc.setFontSize(10.5); doc.setTextColor(34, 43, 69);
      doc.text('Grand Total:', 125, finalY + 38);
      doc.text(`INR ${((inv as any).totalAmount || inv.grandTotal || 0).toLocaleString('en-IN')}`, 190, finalY + 38, { align: 'right' });

      // ── Bank details ─────────────────────────────────────────
      doc.setFontSize(8.5); doc.setTextColor(100, 116, 139);
      doc.text('BANK DETAILS FOR UPI/NEFT:', 15, finalY + 7);
      doc.setFont('helvetica', 'normal');
      doc.text('Bank: Indian Overseas Bank', 15, finalY + 13);
      doc.text('A/C Number: 368002000002002', 15, finalY + 19);
      doc.text('IFSC Code: IOBA0003680', 15, finalY + 25);
      doc.text('UPI ID: praveenit92-3@okaxis', 15, finalY + 31);

      const words = numberToWords((inv as any).totalAmount || inv.grandTotal || 0);
      doc.setFont('helvetica', 'italic'); doc.setTextColor(100, 116, 139);
      doc.text(`Amount in words: ${words}`, 15, finalY + 47);

      doc.setFont('helvetica', 'bold'); doc.setTextColor(51, 65, 85);
      doc.text('Terms & Conditions:', 15, finalY + 57);
      doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(100, 116, 139);
      doc.text('1. All printing works require 50% advance before initialization.', 15, finalY + 62);
      doc.text('2. Delivered items must be verified within 3 days. Standard design terms apply.', 15, finalY + 66);
      doc.text('3. This document is a computer-generated tax invoice and requires signatures.', 15, finalY + 70);

      doc.setDrawColor(203, 213, 225);
      doc.line(145, finalY + 68, 190, finalY + 68);
      doc.setFontSize(8);
      doc.text('Authorized Signatory (Prathitha Digital Printers)', 148, finalY + 72);
    });

    doc.save(`Invoices_${dateRangeStart}_to_${dateRangeEnd}.pdf`);
    setShowDatePicker(false);
  };

  // Generator Identity & Payment Info
  const [preparedBy, setPreparedBy] = useState(() => localStorage.getItem('username') || 'Admin');
  const [paymentStatus, setPaymentStatus] = useState<'Paid' | 'Pending' | 'Partially Paid'>('Paid');
  const [paymentMode, setPaymentMode] = useState<'UPI' | 'Cash' | 'Card' | 'Bank Transfer'>('UPI');

  // Compile list of users whose role is not customer
  const nonCustomerUsers = (users || []).filter((u: any) => u.role !== 'customer');
  const userOptionsSet = new Set<string>();
  userOptionsSet.add('Admin');
  const currentUser = localStorage.getItem('username');
  if (currentUser) {
    userOptionsSet.add(currentUser);
  }
  nonCustomerUsers.forEach((u: any) => {
    if (u.username) userOptionsSet.add(u.username);
  });
  const userOptions = Array.from(userOptionsSet);

  // Client Details state
  const [clientName, setClientName] = useState('');
  const [clientCompany, setClientCompany] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientAddress, setClientAddress] = useState('');
  const [clientGstin, setClientGstin] = useState('');
  const [clientCustomerId, setClientCustomerId] = useState('');

  const [selectedCustomerId, setSelectedCustomerIdState] = useState<string>('');
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);

  const { data: usersResult } = useQuery(GET_ADMIN_USERS, {
    fetchPolicy: 'network-only',
  });
  const customers = React.useMemo(() =>
    (usersResult?.users || []).filter((u: any) => u.role === 'customer'),
    [usersResult]
  );

  // Invoice Metadata
  const [invoiceNo, setInvoiceNo] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [dueDate, setDueDate] = useState(() => {
    const nextWeek = new Date();
    nextWeek.setDate(nextWeek.getDate() + 7);
    return nextWeek.toISOString().split('T')[0];
  });

  // Items State
  const [addedItems, setAddedItems] = useState<InvoiceItem[]>([]);

  // Current Item Config State
  const [itemMode, setItemMode] = useState<'catalog' | 'custom'>('catalog');
  const [selectedProductId, setSelectedProductId] = useState('');
  const [customItemTitle, setCustomItemTitle] = useState('');
  const [customItemDesc, setCustomItemDesc] = useState('');
  const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>({});
  const [bannerWidth, setBannerWidth] = useState<string>('5');
  const [bannerHeight, setBannerHeight] = useState<string>('3');
  const [quantity, setQuantity] = useState<number>(1);
  const [overrideUnitPrice, setOverrideUnitPrice] = useState<string>('');
  const [selectedHsnCode, setSelectedHsnCode] = useState<string>('4911 10 10');
  const [customHsnCode, setCustomHsnCode] = useState<string>('');

  const handleUpdateItemHsnCode = (itemId: string, hsnCode: string) => {
    setAddedItems(prev => prev.map(item => item.id === itemId ? { ...item, hsnCode } : item));
  };

  // Financials State
  const [discountType, setDiscountType] = useState<'percentage' | 'fixed'>('percentage');
  const [discountVal, setDiscountVal] = useState<number>(0);
  const [taxRate, setTaxRate] = useState<number>(18); // Default print tax
  const [shipping, setShipping] = useState<number>(0);

  // Auto-generate invoice number on mount
  useEffect(() => {
    const generateNo = () => {
      const d = new Date();
      const yr = d.getFullYear();
      const mo = String(d.getMonth() + 1).padStart(2, '0');
      const dy = String(d.getDate()).padStart(2, '0');
      const rnd = Math.floor(1000 + Math.random() * 9000);
      return `INV-${yr}${mo}${dy}-${rnd}`;
    };
    setInvoiceNo(generateNo());
  }, []);

  // Invoices are fetched from backend via useQuery

  // Find currently selected product in catalog
  const selectedProduct = products.find(p => p.id === selectedProductId || p.productId === selectedProductId);

  // Reset current selection form
  const resetSelectionForm = () => {
    setSelectedProductId('');
    setCustomItemTitle('');
    setCustomItemDesc('');
    setSelectedOptions({});
    setBannerWidth('5');
    setBannerHeight('3');
    setQuantity(1);
    setOverrideUnitPrice('');
    setSelectedHsnCode(itemMode === 'custom' ? '9989 19' : '4911 10 10');
    setCustomHsnCode('');
  };

  // Trigger default choices when product changes
  useEffect(() => {
    if (selectedProduct) {
      const defaults: Record<string, string> = {};
      selectedProduct.options?.forEach(opt => {
        if (opt.choices && opt.choices.length > 0) {
          defaults[opt.name] = opt.choices[0].label;
        } else if (opt.type === 'number') {
          defaults[opt.name] = String(opt.min || 1);
        } else if (opt.type === 'boolean') {
          defaults[opt.name] = 'false';
        }
      });
      setSelectedOptions(defaults);
      setOverrideUnitPrice('');
      const defaultHsn = getDefaultHsnCode(selectedProduct.name, selectedProduct.category);
      setSelectedHsnCode(defaultHsn);
      setCustomHsnCode('');
    }
  }, [selectedProductId, selectedProduct]);

  // Compute live price of selected catalog product with option choice costs
  const calculateLiveUnitPrice = (): number => {
    if (!selectedProduct) return 0;

    // Check if Flex Banner (which is area-based billing)
    const isFlexBanner = selectedProduct.name.toLowerCase().includes('flex') || selectedProduct.name.toLowerCase().includes('banner');

    let base = selectedProduct.basePrice;
    let extraCosts = 0;

    selectedProduct.options?.forEach(opt => {
      const val = selectedOptions[opt.name];
      if (opt.type === 'select' && opt.choices) {
        const choice = opt.choices.find(c => c.label === val);
        if (choice) {
          extraCosts += choice.extraCost;
        }
      } else if (opt.type === 'boolean' && val === 'true') {
        const choice = opt.choices?.find(c => c.label === 'Yes' || c.label === 'true');
        if (choice) {
          extraCosts += choice.extraCost;
        }
      }
    });

    const singleOptionUnitPrice = base + extraCosts;

    if (isFlexBanner) {
      const w = parseFloat(bannerWidth) || 0;
      const h = parseFloat(bannerHeight) || 0;
      const sqft = w * h;
      return singleOptionUnitPrice * sqft;
    }

    return singleOptionUnitPrice;
  };

  const getProductSpecsText = (): string => {
    if (!selectedProduct) return '';
    const specs: string[] = [];

    const isFlexBanner = selectedProduct.name.toLowerCase().includes('flex') || selectedProduct.name.toLowerCase().includes('banner');
    if (isFlexBanner) {
      specs.push(`Size: ${bannerWidth} x ${bannerHeight} ft (${parseFloat(bannerWidth) * parseFloat(bannerHeight)} sq.ft)`);
    }

    selectedProduct.options?.forEach(opt => {
      const val = selectedOptions[opt.name];
      if (val) {
        specs.push(`${opt.label}: ${val}`);
      }
    });

    return specs.join(' | ');
  };

  const currentComputedUnitPrice = itemMode === 'catalog' ? calculateLiveUnitPrice() : 0;
  const currentDisplayUnitPrice = overrideUnitPrice !== '' ? (parseFloat(overrideUnitPrice) || 0) : currentComputedUnitPrice;

  // Add Item handler
  const handleAddItem = () => {
    let title = '';
    let description = '';
    let unitPrice = 0;
    let originalUnitPrice = 0;
    let unit = 'pcs';

    if (itemMode === 'catalog') {
      if (!selectedProduct) return;
      title = selectedProduct.name;
      description = getProductSpecsText();
      originalUnitPrice = calculateLiveUnitPrice();
      unitPrice = overrideUnitPrice !== '' ? (parseFloat(overrideUnitPrice) || 0) : originalUnitPrice;
      if (selectedProduct.name.toLowerCase().includes('flex') || selectedProduct.name.toLowerCase().includes('banner')) {
        unit = 'sq.ft';
      }
    } else {
      if (!customItemTitle.trim()) return;
      title = customItemTitle.trim();
      description = customItemDesc.trim();
      originalUnitPrice = parseFloat(overrideUnitPrice) || 0;
      unitPrice = originalUnitPrice;
    }

    const finalHsnCode = selectedHsnCode === 'custom' ? (customHsnCode.trim() || '4911 10 10') : (selectedHsnCode || '4911 10 10');

    const newItem: InvoiceItem = {
      id: Math.random().toString(36).substring(2, 9),
      title,
      description,
      quantity,
      unit,
      unitPrice,
      originalUnitPrice,
      isOverridden: unitPrice !== originalUnitPrice,
      total: unitPrice * quantity,
      isCustom: itemMode === 'custom',
      hsnCode: finalHsnCode,
    };

    setAddedItems([...addedItems, newItem]);
    resetSelectionForm();
  };

  // Remove Item
  const handleRemoveItem = (id: string) => {
    setAddedItems(addedItems.filter(item => item.id !== id));
  };

  // Financial calculations
  const subtotal = addedItems.reduce((acc, curr) => acc + curr.total, 0);

  const discountAmount = discountType === 'percentage'
    ? subtotal * (discountVal / 100)
    : discountVal;

  const afterDiscount = Math.max(0, subtotal - discountAmount);
  const taxAmount = afterDiscount * (taxRate / 100);
  const grandTotal = Math.round(afterDiscount + taxAmount + shipping);

  // Save Invoice
  const handleSaveInvoice = () => {
    if (!invoiceNo.trim()) {
      alert('Please provide an Invoice Number.');
      return;
    }

    const newInvoice: SavedInvoice = {
      invoiceNo: invoiceNo.trim(),
      date: invoiceDate,
      dueDate,
      preparedBy,
      paymentStatus,
      paymentMode,
      clientInfo: {
        name: clientName || 'Walk-in Customer',
        customerId: clientCustomerId,
        company: clientCompany,
        phone: clientPhone,
        email: clientEmail,
        address: clientAddress,
        gstin: clientGstin,
      },
      items: addedItems,
      subtotal,
      discountType,
      discountVal,
      discountAmount,
      taxRate,
      taxAmount,
      shipping,
      grandTotal,
      savedAt: new Date().toISOString(),
    };

    // Format new invoice to strictly match backend schema types
    const backendInput = {
      invoiceNo: String(newInvoice.invoiceNo).trim(),
      date: String(newInvoice.date || new Date().toISOString().split('T')[0]),
      dueDate: String(newInvoice.dueDate || ''),
      preparedBy: String(newInvoice.preparedBy || 'Admin'),
      paymentStatus: newInvoice.paymentStatus || 'Pending',
      paymentMode: newInvoice.paymentMode || 'UPI',
      clientInfo: {
        name: String(clientName || 'Walk-in Customer').trim(),
        customerId: clientCustomerId ? String(clientCustomerId).trim() : null,
        company: String(clientCompany || '').trim(),
        phone: String(clientPhone || '').trim(),
        email: String(clientEmail || '').trim(),
        address: String(clientAddress || '').trim(),
        gstin: String(clientGstin || '').trim(),
      },
      items: newInvoice.items.map(i => ({
        id: String(i.id || Math.random().toString(36).substring(2, 9)),
        description: `${i.title || ''}\n${i.description || ''}`.trim() || 'Printing Item',
        quantity: Math.max(1, Number(i.quantity) || 1),
        unitPrice: Math.max(0, Number(i.unitPrice) || 0),
        totalPrice: Math.max(0, Number(i.total ?? (Number(i.unitPrice || 0) * Number(i.quantity || 1))) || 0),
        isCustom: Boolean(i.isCustom),
        hsnCode: String(i.hsnCode || '4911 10 10')
      })),
      subtotal: Math.max(0, Number(newInvoice.subtotal) || 0),
      discountType: newInvoice.discountType || 'percentage',
      discountVal: Math.max(0, Number(newInvoice.discountVal) || 0),
      discountAmount: Math.max(0, Number(newInvoice.discountAmount) || 0),
      taxType: 'none',
      taxRate: Math.max(0, Number(newInvoice.taxRate) || 0),
      taxAmount: Math.max(0, Number(newInvoice.taxAmount) || 0),
      totalAmount: Math.max(0, Number(newInvoice.grandTotal) || 0),
      amountPaid: newInvoice.paymentStatus === 'Paid' ? Math.max(0, Number(newInvoice.grandTotal) || 0) : 0,
      balanceDue: newInvoice.paymentStatus === 'Paid' ? 0 : Math.max(0, Number(newInvoice.grandTotal) || 0),
      notes: '',
      terms: ''
    };

    const isEditing = invoices.some(inv => inv.invoiceNo === newInvoice.invoiceNo);

    const resetForm = () => {
      const nextNo = `INV-${new Date().getFullYear()}${String(new Date().getMonth() + 1).padStart(2, '0')}${String(new Date().getDate()).padStart(2, '0')}-${Math.floor(1000 + Math.random() * 9000)}`;
      setInvoiceNo(nextNo);

      setSelectedCustomerIdState('');
      setClientName('');
      setClientCompany('');
      setClientPhone('');
      setClientEmail('');
      setClientAddress('');
      setClientGstin('');
      setClientCustomerId('');
      setAddedItems([]);
      setDiscountVal(0);
      setShipping(0);
    };

    if (isEditing) {
      updateInvoice({ variables: { invoiceNo: newInvoice.invoiceNo, input: backendInput } })
        .then((res) => {
          if (res.data?.updateInvoice?.success) {
            alert('Invoice updated successfully!');
            refetchInvoices();
            resetForm();
            setActiveSubTab('history');
          } else {
            alert('Error: ' + res.data?.updateInvoice?.message);
          }
        })
        .catch((err: any) => {
          const detailedMsg = err.graphQLErrors?.map((g: any) => g.message).join('; ') || err.networkError?.message || err.message || 'Unknown error';
          alert('Server error: ' + detailedMsg);
          console.error('Invoice save error:', err);
        });
    } else {
      createInvoice({ variables: { input: backendInput } })
        .then((res) => {
          if (res.data?.createInvoice?.success) {
            alert('Invoice saved successfully to database!');
            refetchInvoices();
            resetForm();
          } else {
            alert('Error: ' + res.data?.createInvoice?.message);
          }
        })
        .catch((err: any) => {
          const detailedMsg = err.graphQLErrors?.map((g: any) => g.message).join('; ') || err.networkError?.message || err.message || 'Unknown error';
          alert('Server error: ' + detailedMsg);
          console.error('Invoice save error:', err);
        });
    }
  };

  const handleDeleteInvoice = (invNo: string) => {
    if (window.confirm(`Are you sure you want to delete Invoice ${invNo}?`)) {
      deleteInvoice({ variables: { invoiceNo: invNo } })
        .then(() => {
          refetchInvoices();
        })
        .catch((err) => {
          alert('Failed to delete: ' + err.message);
        });
    }
  };

  const handleEditInvoice = (inv: any) => {
    setInvoiceNo(inv.invoiceNo);
    setInvoiceDate(inv.date || new Date().toISOString().split('T')[0]);
    setDueDate(inv.dueDate || '');
    setPreparedBy(inv.preparedBy || localStorage.getItem('username') || 'Admin');
    setPaymentStatus(inv.paymentStatus || 'Pending');
    setPaymentMode(inv.paymentMode || 'UPI');
    setClientName(inv.clientInfo?.name || '');
    setClientCompany(inv.clientInfo?.company || '');
    setClientPhone(inv.clientInfo?.phone || '');
    setClientEmail(inv.clientInfo?.email || '');
    setClientAddress(inv.clientInfo?.address || '');
    setClientGstin(inv.clientInfo?.gstin || '');
    setClientCustomerId(inv.clientInfo?.customerId || '');

    const parsedItems = (inv.items || []).map((i: any, idx: number) => {
      const parts = i.description ? i.description.split('\n') : ['Item'];
      return {
        id: i.id || Date.now().toString() + idx,
        title: i.title || parts[0],
        description: parts.slice(1).join('\n'),
        quantity: i.quantity || 1,
        unitPrice: i.unitPrice || 0,
        total: i.totalPrice || i.total || (i.unitPrice * (i.quantity || 1)) || 0,
        isCustom: i.isCustom || false,
        unit: i.unit || 'pcs',
        hsnCode: i.hsnCode || '4911 10 10',
      };
    });
    setAddedItems(parsedItems);

    setDiscountType(inv.discountType || 'percentage');
    setDiscountVal(inv.discountVal || 0);
    setTaxRate(inv.taxRate || 18);
    setShipping(inv.shipping || 0);

    setActiveSubTab('create');
  };

  // PDF Export Engine (Customized for TAX INVOICES)
  const handleDownloadPDF = (inv: SavedInvoice) => {
    const doc = new jsPDF();

    // 1. Premium Branded Header
    doc.setFillColor(34, 43, 69); // Dark Slate Blue primary
    doc.rect(0, 0, 210, 40, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(22);
    doc.text('PRATHITHA DIGITAL PRINTERS', 15, 17);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text('Premium Digital & Flex Printing Services | Eco-Solvent & Corporate Works', 15, 24);
    doc.text('GSTIN: 33CHVPP2054B1ZE | Ph: +91 91508 08130 | Chennai, Tamil Nadu', 15, 29);

    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('TAX INVOICE', 150, 25);

    // 2. Invoice Details Panel
    doc.setTextColor(51, 65, 85);
    doc.setFontSize(9);

    // Left side: Invoice metadata
    doc.setFont('helvetica', 'bold');
    doc.text('INVOICE METADATA:', 15, 50);
    doc.setFont('helvetica', 'normal');
    doc.text(`Invoice No: ${inv.invoiceNo}`, 15, 56);
    doc.text(`Invoice Date: ${inv.date}`, 15, 62);
    doc.text(`Due Date: ${inv.dueDate}`, 15, 68);
    doc.text(`Prepared By: ${inv.preparedBy}`, 15, 74);

    // Payment Info
    doc.setFont('helvetica', 'bold');
    doc.text(`Payment Mode: ${inv.paymentMode}`, 15, 82);

    // Right side: Billing information
    doc.setFont('helvetica', 'bold');
    doc.text('BILL TO:', 120, 50);
    doc.setFont('helvetica', 'normal');
    doc.text(`Customer Name: ${inv.clientInfo.name}`, 120, 56);
    if (inv.clientInfo.customerId) {
      doc.text(`Customer ID: ${inv.clientInfo.customerId}`, 120, 62);
    }

    let yAddress = inv.clientInfo.customerId ? 68 : 62;
    if (inv.clientInfo.company) {
      doc.text(`Company: ${inv.clientInfo.company}`, 120, yAddress);
      yAddress += 6;
    }

    const contactStr = [inv.clientInfo.phone, inv.clientInfo.email].filter(Boolean).join(' | ');
    doc.text(contactStr, 120, yAddress);
    yAddress += 6;

    if (inv.clientInfo.address) {
      doc.text(`Address: ${inv.clientInfo.address}`, 120, yAddress);
      yAddress += 6;
    }
    if (inv.clientInfo.gstin) {
      doc.text(`GSTIN: ${inv.clientInfo.gstin}`, 120, yAddress);
    }

    // Payment Status Stamp/Badge on top right
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    if (inv.paymentStatus === 'Paid') {
      doc.setFillColor(220, 252, 231); // Light green background
      doc.rect(155, 33, 40, 8, 'F');
      doc.setTextColor(22, 101, 52); // Dark green text
      doc.text('PAID', 170, 39);
    } else if (inv.paymentStatus === 'Partially Paid') {
      doc.setFillColor(254, 243, 199); // Light yellow
      doc.rect(150, 33, 45, 8, 'F');
      doc.setTextColor(146, 64, 14); // Orange text
      doc.text('PARTIALLY PAID', 153, 39);
    } else {
      doc.setFillColor(254, 226, 226); // Light red
      doc.rect(155, 33, 40, 8, 'F');
      doc.setTextColor(153, 27, 27); // Dark red text
      doc.text('PENDING', 165, 39);
    }

    // Reset standard styling
    doc.setTextColor(51, 65, 85);
    doc.setFontSize(9);

    // Divider Line
    doc.setDrawColor(226, 232, 240);
    doc.line(15, 90, 195, 90);

    // 3. Line Items Table using autoTable
    const tableHeaders = [['S.No', 'Product & Specs', 'HSN/SAC', 'Qty', 'Unit', 'Unit Price', 'Total']];
    const tableRows = inv.items.map((item: any, idx) => [
      idx + 1,
      item.title ? `${item.title}\n${item.description || ''}` : (item.description || ''),
      item.hsnCode || '4911 10 10',
      item.quantity || 0,
      item.unit || '-',
      `INR ${(item.unitPrice || 0).toLocaleString('en-IN')}`,
      `INR ${(item.totalPrice || item.total || 0).toLocaleString('en-IN')}`
    ]);

    autoTable(doc, {
      startY: 95,
      head: tableHeaders,
      body: tableRows,
      theme: 'striped',
      headStyles: { fillStyle: 'F', fillColor: [34, 43, 69], textColor: [255, 255, 255], fontStyle: 'bold' },
      columnStyles: {
        0: { width: 10 },
        1: { width: 70 },
        2: { width: 20, halign: 'center' },
        3: { width: 15, halign: 'center' },
        4: { width: 15, halign: 'center' },
        5: { width: 30, halign: 'right' },
        6: { width: 30, halign: 'right' }
      },
      styles: { fontSize: 8.5, cellPadding: 3.5, overflow: 'linebreak' }
    });

    // 4. Financial Calculations Summary Panel
    let finalY = (doc as any).lastAutoTable.finalY + 10;

    // Check page overflow
    if (finalY > 210) {
      doc.addPage();
      finalY = 20;
    }

    doc.setFillColor(248, 250, 252);
    doc.rect(120, finalY, 75, 42, 'F');
    doc.setDrawColor(226, 232, 240);
    doc.rect(120, finalY, 75, 42, 'S');

    doc.setTextColor(100, 116, 139);
    doc.text('Subtotal:', 125, finalY + 7);
    doc.text(`Discount (${inv.discountType === 'percentage' ? inv.discountVal + '%' : 'Fixed'}):`, 125, finalY + 14);
    doc.text(`GST Tax (${inv.taxRate}%):`, 125, finalY + 21);
    doc.text('Delivery/Shipping:', 125, finalY + 28);

    doc.setTextColor(51, 65, 85);
    doc.text(`INR ${(inv.subtotal || 0).toLocaleString('en-IN')}`, 190, finalY + 7, { align: 'right' });
    doc.text(`- INR ${(inv.discountAmount || 0).toLocaleString('en-IN')}`, 190, finalY + 14, { align: 'right' });
    doc.text(`INR ${(inv.taxAmount || 0).toLocaleString('en-IN')}`, 190, finalY + 21, { align: 'right' });
    doc.text(`INR ${(inv.shipping || 0).toLocaleString('en-IN')}`, 190, finalY + 28, { align: 'right' });

    // Grand Total Line
    doc.line(122, finalY + 32, 193, finalY + 32);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10.5);
    doc.setTextColor(34, 43, 69);
    doc.text('Grand Total:', 125, finalY + 38);
    doc.text(`INR ${(inv.totalAmount || inv.grandTotal || 0).toLocaleString('en-IN')}`, 190, finalY + 38, { align: 'right' });

    // Bank Details on Left Side
    doc.setFontSize(8.5);
    doc.setTextColor(100, 116, 139);
    doc.text('BANK DETAILS FOR UPI/NEFT:', 15, finalY + 7);
    doc.setFont('helvetica', 'normal');
    doc.text('Bank: Indian Overseas Bank', 15, finalY + 13);
    doc.text('A/C Number: 368002000002002', 15, finalY + 19);
    doc.text('IFSC Code: IOBA0003680', 15, finalY + 25);
    doc.text('UPI ID: praveenit92-3@okaxis', 15, finalY + 31);

    // Number in words
    const words = numberToWords(inv.totalAmount || inv.grandTotal || 0);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(100, 116, 139);
    doc.text(`Amount in words: ${words}`, 15, finalY + 47);

    // Footer Terms
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(51, 65, 85);
    doc.text('Terms & Conditions:', 15, finalY + 57);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(100, 116, 139);
    doc.text('1. All printing works require 50% advance before initialization.', 15, finalY + 62);
    doc.text('2. Delivered items must be verified within 3 days. Standard design terms apply.', 15, finalY + 66);
    doc.text('3. This document is a computer-generated tax invoice and requires signatures.', 15, finalY + 70);

    // Signature boxes
    doc.setDrawColor(203, 213, 225);
    doc.line(145, finalY + 68, 190, finalY + 68);
    doc.setFontSize(8);
    doc.text('Authorized Signatory (Prathitha Digital Printers)', 148, finalY + 72);

    doc.save(`${inv.invoiceNo}.pdf`);
  };

  // Excel Export Module
  const handleExportExcel = (inv: SavedInvoice) => {
    const headerData = [
      ['TAX INVOICE'],
      [],
      ['Invoice Details'],
      ['Invoice No', inv.invoiceNo],
      ['Invoice Date', inv.date],
      ['Due Date', inv.dueDate],
      ['Payment Status', inv.paymentStatus],
      ['Payment Mode', inv.paymentMode],
      ['Prepared By', inv.preparedBy],
      [],
      ['Billing Information'],
      ['Customer Name', inv.clientInfo.name],
      ['Customer ID', inv.clientInfo.customerId || 'N/A'],
      ['Company', inv.clientInfo.company || 'N/A'],
      ['Phone', inv.clientInfo.phone || 'N/A'],
      ['Email', inv.clientInfo.email || 'N/A'],
      ['Address', inv.clientInfo.address || 'N/A'],
      ['GSTIN', inv.clientInfo.gstin || 'N/A'],
      [],
      ['Line Items'],
      ['S.No', 'Item', 'HSN/SAC', 'Specs', 'Qty', 'Unit', 'Unit Price', 'Total']
    ];

    const rowData = inv.items.map((item: any, idx) => [
      idx + 1,
      item.title || 'Item',
      item.hsnCode || '4911 10 10',
      item.description || '',
      item.quantity || 0,
      item.unit || '-',
      item.unitPrice || 0,
      item.totalPrice || item.total || 0
    ]);

    const footerData = [
      [],
      ['Summary'],
      ['Subtotal', inv.subtotal],
      ['Discount Amount', inv.discountAmount],
      [`GST Tax (${inv.taxRate}%)`, inv.taxAmount],
      ['Shipping', inv.shipping],
      ['Grand Total', inv.totalAmount || inv.grandTotal || 0]
    ];

    const combined = [...headerData, ...rowData, ...footerData];
    const ws = XLSX.utils.aoa_to_sheet(combined);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Invoice');
    XLSX.writeFile(wb, `${inv.invoiceNo}.xlsx`);
  };

  return (
    <Card className="border-0 shadow-sm rounded-4">
      <Card.Body className="p-4">
        <div className="d-flex align-items-center justify-content-between mb-4 border-bottom pb-3">
          <div>
            <h4 className="fw-bold text-dark mb-1">Invoice Generator</h4>
            <p className="text-muted mb-0 style-desc" style={{ fontSize: '12.5px' }}>
              Compile, record, and print professional GST Tax Invoices for retail & corporate orders.
            </p>
          </div>
          <div className="d-flex gap-2">
            <Button
              variant={activeSubTab === 'create' ? 'primary' : 'outline-primary'}
              onClick={() => setActiveSubTab('create')}
              className="fw-semibold px-3 py-1.5"
              style={{ borderRadius: '8px', fontSize: '13px' }}
            >
              <i className="fas fa-plus me-1.5" /> Create Invoice
            </Button>
            <Button
              variant={activeSubTab === 'history' ? 'primary' : 'outline-primary'}
              onClick={() => setActiveSubTab('history')}
              className="fw-semibold px-3 py-1.5"
              style={{ borderRadius: '8px', fontSize: '13px' }}
            >
              <i className="fas fa-history me-1.5" /> Invoice History ({invoices.length})
            </Button>
          </div>
        </div>

        {activeSubTab === 'create' ? (
          <div>
            <div className="bg-light p-3 rounded-4 border mb-4">
              <h6 className="fw-bold text-dark mb-3"><i className="fas fa-file-alt text-primary me-2" />1. Invoice & Payment Metadata</h6>
              <Row className="g-3">
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '11px' }}>Invoice No *</Form.Label>
                    <Form.Control
                      type="text"
                      size="sm"
                      value={invoiceNo}
                      onChange={(e) => setInvoiceNo(e.target.value)}
                      style={{ borderRadius: '8px' }}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '11px' }}>Invoice Date *</Form.Label>
                    <Form.Control
                      type="date"
                      size="sm"
                      value={invoiceDate}
                      onChange={(e) => setInvoiceDate(e.target.value)}
                      style={{ borderRadius: '8px' }}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '11px' }}>Due Date *</Form.Label>
                    <Form.Control
                      type="date"
                      size="sm"
                      value={dueDate}
                      onChange={(e) => setDueDate(e.target.value)}
                      style={{ borderRadius: '8px' }}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '11px' }}>Prepared By</Form.Label>
                    <Form.Select
                      size="sm"
                      value={preparedBy}
                      onChange={(e) => setPreparedBy(e.target.value)}
                      style={{ borderRadius: '8px' }}
                    >
                      {userOptions.map((optName) => (
                        <option key={optName} value={optName}>
                          {optName}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '11px' }}>Payment Status</Form.Label>
                    <Form.Select
                      size="sm"
                      value={paymentStatus}
                      onChange={(e) => setPaymentStatus(e.target.value as any)}
                      style={{ borderRadius: '8px' }}
                    >
                      <option value="Paid">Paid</option>
                      <option value="Pending">Pending</option>
                      <option value="Partially Paid">Partially Paid</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '11px' }}>Payment Mode</Form.Label>
                    <Form.Select
                      size="sm"
                      value={paymentMode}
                      onChange={(e) => setPaymentMode(e.target.value as any)}
                      style={{ borderRadius: '8px' }}
                    >
                      <option value="UPI">UPI</option>
                      <option value="Cash">Cash</option>
                      <option value="Card">Card</option>
                      <option value="Bank Transfer">Bank Transfer</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            </div>

            <div className="bg-light p-3 rounded-4 border mb-4">
              <h6 className="fw-bold text-dark mb-3"><i className="fas fa-user-tie text-primary me-2" />2. Customer Information</h6>
              <Row className="g-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label className="fw-semibold text-muted d-flex justify-content-between align-items-center mb-1" style={{ fontSize: '11px' }}>
                      <span>Select Profile Customer</span>
                      <Button
                        variant="link"
                        className="p-0 text-decoration-none fw-semibold"
                        style={{ fontSize: '11px' }}
                        onClick={() => setShowAddCustomerModal(true)}
                      >
                        <i className="fas fa-plus me-1"></i>New Profile Customer
                      </Button>
                    </Form.Label>
                    <Form.Select
                      size="sm"
                      value={selectedCustomerId}
                      onChange={(e) => {
                        const val = e.target.value;
                        setSelectedCustomerIdState(val);
                        const selected = customers.find((c: any) => c.id === val);
                        if (selected) {
                          setClientName(selected.username || '');
                          setClientCustomerId(selected.customerId || '');
                          setClientCompany(selected.companyName || '');
                          setClientPhone(selected.phone || '');
                          setClientEmail(selected.email || '');
                          setClientAddress(selected.companyAddress || selected.shippingAddress || '');
                          setClientGstin(selected.gstNumber || '');
                        } else {
                          setClientName('');
                          setClientCustomerId('');
                          setClientCompany('');
                          setClientPhone('');
                          setClientEmail('');
                          setClientAddress('');
                          setClientGstin('');
                        }
                      }}
                      style={{ borderRadius: '8px' }}
                    >
                      <option value="">-- Select Existing Customer (or Add New) --</option>
                      {customers.map((c: any) => (
                        <option key={c.id} value={c.id}>
                          {c.username} {c.companyName ? `(${c.companyName})` : ''} - {c.phone || 'No phone'}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '11px' }}>Customer Name *</Form.Label>
                    <Form.Control
                      type="text"
                      size="sm"
                      placeholder="e.g. John Doe"
                      value={clientName}
                      onChange={(e) => setClientName(e.target.value)}
                      style={{ borderRadius: '8px' }}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '11px' }}>Company Name</Form.Label>
                    <Form.Control
                      type="text"
                      size="sm"
                      placeholder="e.g. Acme Prints"
                      value={clientCompany}
                      onChange={(e) => setClientCompany(e.target.value)}
                      style={{ borderRadius: '8px' }}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '11px' }}>Phone Number</Form.Label>
                    <Form.Control
                      type="text"
                      size="sm"
                      placeholder="e.g. 9876543210"
                      value={clientPhone}
                      onChange={(e) => setClientPhone(e.target.value)}
                      style={{ borderRadius: '8px' }}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '11px' }}>Email Address</Form.Label>
                    <Form.Control
                      type="email"
                      size="sm"
                      placeholder="e.g. customer@gmail.com"
                      value={clientEmail}
                      onChange={(e) => setClientEmail(e.target.value)}
                      style={{ borderRadius: '8px' }}
                    />
                  </Form.Group>
                </Col>
                <Col md={8}>
                  <Form.Group>
                    <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '11px' }}>Billing Address</Form.Label>
                    <Form.Control
                      type="text"
                      size="sm"
                      placeholder="e.g. 12/4, Station Road, Chennai"
                      value={clientAddress}
                      onChange={(e) => setClientAddress(e.target.value)}
                      style={{ borderRadius: '8px' }}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '11px' }}>Customer GSTIN</Form.Label>
                    <Form.Control
                      type="text"
                      size="sm"
                      placeholder="e.g. 33AAAAA1111A1Z1"
                      value={clientGstin}
                      onChange={(e) => setClientGstin(e.target.value)}
                      style={{ borderRadius: '8px' }}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>

            <div className="bg-light p-3 rounded-4 border mb-4">
              <h6 className="fw-bold text-dark mb-3"><i className="fas fa-cart-plus text-primary me-2" />3. Line Items Builder</h6>

              <div className="d-flex gap-2 mb-3 bg-white p-2 rounded-3 border">
                <button
                  type="button"
                  onClick={() => { setItemMode('catalog'); resetSelectionForm(); }}
                  className={`btn py-1.5 px-3 flex-fill border ${itemMode === 'catalog' ? 'btn-primary border-primary fw-semibold' : 'btn-outline-secondary border-0 bg-white text-muted'}`}
                  style={{ borderRadius: '8px', fontSize: '12px' }}
                >
                  <i className="fas fa-boxes me-2" /> Catalog Product
                </button>
                <button
                  type="button"
                  onClick={() => { setItemMode('custom'); resetSelectionForm(); }}
                  className={`btn py-1.5 px-3 flex-fill border ${itemMode === 'custom' ? 'btn-primary border-primary fw-semibold' : 'btn-outline-secondary border-0 bg-white text-muted'}`}
                  style={{ borderRadius: '8px', fontSize: '12px' }}
                >
                  <i className="fas fa-edit me-2" /> Manual Custom Item
                </button>
              </div>

              <Row className="g-3">
                {itemMode === 'catalog' ? (
                  <>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '11px' }}>Choose Product *</Form.Label>
                        <Form.Select
                          size="sm"
                          value={selectedProductId}
                          onChange={(e) => setSelectedProductId(e.target.value)}
                          style={{ borderRadius: '8px' }}
                        >
                          <option value="">-- Select Standard Product --</option>
                          {products.map(p => (
                            <option key={p.id || p.productId} value={p.id || p.productId}>{p.name}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    {selectedProduct && selectedProduct.options?.map(opt => (
                      <Col md={3} key={opt.name}>
                        <Form.Group>
                          <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '11px' }}>{opt.label}</Form.Label>
                          {opt.type === 'select' ? (
                            <Form.Select
                              size="sm"
                              value={selectedOptions[opt.name] || ''}
                              onChange={(e) => setSelectedOptions({ ...selectedOptions, [opt.name]: e.target.value })}
                              style={{ borderRadius: '8px' }}
                            >
                              {opt.choices?.map(c => (
                                <option key={c.label} value={c.label}>{c.label} (+₹{c.extraCost})</option>
                              ))}
                            </Form.Select>
                          ) : (
                            <Form.Control
                              type="text"
                              size="sm"
                              value={selectedOptions[opt.name] || ''}
                              onChange={(e) => setSelectedOptions({ ...selectedOptions, [opt.name]: e.target.value })}
                              style={{ borderRadius: '8px' }}
                            />
                          )}
                        </Form.Group>
                      </Col>
                    ))}
                    {selectedProduct && (selectedProduct.name.toLowerCase().includes('flex') || selectedProduct.name.toLowerCase().includes('banner')) && (
                      <>
                        <Col md={2}>
                          <Form.Group>
                            <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '11px' }}>Width (ft) *</Form.Label>
                            <Form.Control
                              type="number"
                              size="sm"
                              value={bannerWidth}
                              onChange={(e) => setBannerWidth(e.target.value)}
                              style={{ borderRadius: '8px' }}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={2}>
                          <Form.Group>
                            <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '11px' }}>Height (ft) *</Form.Label>
                            <Form.Control
                              type="number"
                              size="sm"
                              value={bannerHeight}
                              onChange={(e) => setBannerHeight(e.target.value)}
                              style={{ borderRadius: '8px' }}
                            />
                          </Form.Group>
                        </Col>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '11px' }}>Item Title *</Form.Label>
                        <Form.Control
                          type="text"
                          size="sm"
                          placeholder="e.g. Custom Book Binding"
                          value={customItemTitle}
                          onChange={(e) => setCustomItemTitle(e.target.value)}
                          style={{ borderRadius: '8px' }}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={5}>
                      <Form.Group>
                        <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '11px' }}>Specifications/Description</Form.Label>
                        <Form.Control
                          type="text"
                          size="sm"
                          placeholder="e.g. Hard binding with golden letter print"
                          value={customItemDesc}
                          onChange={(e) => setCustomItemDesc(e.target.value)}
                          style={{ borderRadius: '8px' }}
                        />
                      </Form.Group>
                    </Col>
                  </>
                )}

                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="fw-semibold text-muted mb-1 d-flex justify-content-between align-items-center" style={{ fontSize: '11px' }}>
                      <span>HSN / SAC Code (GST) *</span>
                      <span className="badge bg-primary-subtle text-primary border border-primary-subtle" style={{ fontSize: '9px' }}>GST Products</span>
                    </Form.Label>
                    <Form.Select
                      size="sm"
                      value={selectedHsnCode}
                      onChange={(e) => setSelectedHsnCode(e.target.value)}
                      style={{ borderRadius: '8px' }}
                    >
                      {DETAILED_HSN_CATEGORIES.map(category => (
                        <optgroup key={category.category} label={category.category}>
                          {category.items.map(h => (
                            <option key={h.code} value={h.code}>
                              {h.label} ({h.gstRate}% GST)
                            </option>
                          ))}
                        </optgroup>
                      ))}
                      <option value="custom">✏️ Enter Custom HSN / SAC...</option>
                    </Form.Select>
                  </Form.Group>
                  {selectedHsnCode === 'custom' ? (
                    <Form.Control
                      type="text"
                      size="sm"
                      className="mt-1"
                      placeholder="e.g. 4911 10 10 or 9989 19"
                      value={customHsnCode}
                      onChange={(e) => setCustomHsnCode(e.target.value)}
                      style={{ borderRadius: '8px', fontSize: '11.5px' }}
                    />
                  ) : (
                    (() => {
                      const cur = ALL_DETAILED_HSN_CODES.find(h => h.code === selectedHsnCode);
                      if (cur) {
                        return (
                          <div className="mt-1 d-flex align-items-center gap-1.5" style={{ fontSize: '11px', color: '#475569' }}>
                            <span className="badge bg-primary-subtle text-primary border border-primary-subtle" style={{ fontSize: '9.5px', fontWeight: 600 }}>
                              {cur.gstRate}% GST
                            </span>
                            <span className="text-truncate" title={cur.description}>{cur.description}</span>
                          </div>
                        );
                      }
                      return null;
                    })()
                  )}
                </Col>

                <Col md={1.5}>
                  <Form.Group>
                    <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '11px' }}>Quantity *</Form.Label>
                    <Form.Control
                      type="number"
                      size="sm"
                      min={1}
                      value={quantity}
                      onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                      style={{ borderRadius: '8px' }}
                    />
                  </Form.Group>
                </Col>

                <Col md={2}>
                  <Form.Group>
                    <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '11px' }}>
                      {itemMode === 'catalog' ? `Override Price (Base: ₹${currentComputedUnitPrice})` : 'Unit Price (₹) *'}
                    </Form.Label>
                    <Form.Control
                      type="number"
                      size="sm"
                      placeholder={itemMode === 'catalog' ? 'Leave blank for base' : 'e.g. 150'}
                      value={overrideUnitPrice}
                      onChange={(e) => setOverrideUnitPrice(e.target.value)}
                      style={{ borderRadius: '8px' }}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <div className="d-flex justify-content-end mt-3">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleAddItem}
                  className="fw-bold px-4 py-2"
                  style={{ borderRadius: '8px' }}
                  disabled={itemMode === 'catalog' ? !selectedProductId : !customItemTitle.trim()}
                >
                  <i className="fas fa-plus me-1.5" /> Add to Invoice
                </Button>
              </div>
            </div>

            {/* Added Items List */}
            <div className="mb-4">
              <h6 className="fw-bold text-dark mb-3"><i className="fas fa-list text-primary me-2" />4. Invoice Line Items</h6>
              {addedItems.length === 0 ? (
                <Alert variant="warning" className="text-center rounded-3 border-0 py-3 mb-0" style={{ fontSize: '13px' }}>
                  <i className="fas fa-info-circle me-1.5" /> No items added to invoice yet. Add catalog or custom items above.
                </Alert>
              ) : (
                <div className="table-responsive bg-white rounded-3 border">
                  <Table hover className="align-middle mb-0" style={{ fontSize: '13px' }}>
                    <thead className="table-light">
                      <tr>
                        <th className="py-2.5 px-3">S.No</th>
                        <th className="py-2.5">Item & Details</th>
                        <th className="py-2.5" style={{ minWidth: '220px' }}>HSN / SAC Code (GST)</th>
                        <th className="py-2.5 text-center">Qty</th>
                        <th className="py-2.5 text-center">Unit</th>
                        <th className="py-2.5 text-end">Unit Price</th>
                        <th className="py-2.5 text-end">Total Price</th>
                        <th className="py-2.5 text-end px-3">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {addedItems.map((item, idx) => (
                        <tr key={item.id}>
                          <td className="py-2 px-3 fw-bold">{idx + 1}</td>
                          <td>
                            <div className="fw-semibold text-dark">{item.title}</div>
                            {item.description && <div className="text-muted" style={{ fontSize: '11px' }}>{item.description}</div>}
                          </td>
                          <td>
                            <Form.Select
                              size="sm"
                              value={item.hsnCode || '4911 10 10'}
                              onChange={(e) => {
                                const val = e.target.value;
                                if (val === 'custom') {
                                  const customVal = window.prompt('Enter custom HSN / SAC Code for GST:', item.hsnCode || '');
                                  if (customVal && customVal.trim()) {
                                    handleUpdateItemHsnCode(item.id, customVal.trim());
                                  }
                                } else {
                                  handleUpdateItemHsnCode(item.id, val);
                                }
                              }}
                              style={{ borderRadius: '8px', fontSize: '11.5px', padding: '3px 8px', maxWidth: '240px' }}
                              title="Choose HSN/SAC Code for GST taxation"
                            >
                              {DETAILED_HSN_CATEGORIES.map(category => (
                                <optgroup key={category.category} label={category.category}>
                                  {category.items.map(h => (
                                    <option key={h.code} value={h.code}>
                                      {h.label} ({h.gstRate}% GST)
                                    </option>
                                  ))}
                                </optgroup>
                              ))}
                              {item.hsnCode && !ALL_DETAILED_HSN_CODES.some(h => h.code === item.hsnCode) && (
                                <option value={item.hsnCode}>{item.hsnCode} (Custom / Other)</option>
                              )}
                              <option value="custom">✏️ Custom Code...</option>
                            </Form.Select>
                            {(() => {
                              const found = ALL_DETAILED_HSN_CODES.find(h => h.code === item.hsnCode);
                              if (found) {
                                return (
                                  <div className="text-muted mt-1 d-flex align-items-center gap-1" style={{ fontSize: '10px' }}>
                                    <span className="badge bg-secondary-subtle text-secondary" style={{ fontSize: '9px' }}>{found.gstRate}% GST</span>
                                    <span className="text-truncate" style={{ maxWidth: '170px' }} title={found.description}>{found.description}</span>
                                  </div>
                                );
                              }
                              return null;
                            })()}
                          </td>
                          <td className="text-center">{item.quantity}</td>
                          <td className="text-center text-capitalize">{item.unit}</td>
                          <td className="text-end">
                            {item.isOverridden ? (
                              <div>
                                <span className="text-decoration-line-through text-muted me-1.5" style={{ fontSize: '11px' }}>₹{item.originalUnitPrice}</span>
                                <span className="text-danger fw-semibold">₹{item.unitPrice}</span>
                              </div>
                            ) : (
                              <span>₹{item.unitPrice}</span>
                            )}
                          </td>
                          <td className="text-end fw-bold text-dark">₹{item.total.toLocaleString('en-IN')}</td>
                          <td className="text-end px-3">
                            <Button
                              variant="outline-danger"
                              size="sm"
                              className="py-0.5 px-1.5 border-0"
                              onClick={() => handleRemoveItem(item.id)}
                            >
                              <i className="fas fa-trash-alt" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </div>

            {/* Calculations & Save Section */}
            <Row className="g-4">
              <Col md={6}>
                <div className="bg-light p-3 rounded-4 border h-100">
                  <h6 className="fw-bold text-dark mb-3"><i className="fas fa-percent text-primary me-2" />5. Taxes & Deductions</h6>
                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '11px' }}>Discount Type</Form.Label>
                        <div className="d-flex gap-2">
                          <Button
                            variant={discountType === 'percentage' ? 'primary' : 'outline-primary'}
                            size="sm"
                            className="flex-fill"
                            onClick={() => { setDiscountType('percentage'); setDiscountVal(0); }}
                          >
                            Percentage
                          </Button>
                          <Button
                            variant={discountType === 'fixed' ? 'primary' : 'outline-primary'}
                            size="sm"
                            className="flex-fill"
                            onClick={() => { setDiscountType('fixed'); setDiscountVal(0); }}
                          >
                            Fixed Amount
                          </Button>
                        </div>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '11px' }}>
                          Discount Value ({discountType === 'percentage' ? '%' : '₹'})
                        </Form.Label>
                        <Form.Control
                          type="number"
                          size="sm"
                          min={0}
                          value={discountVal}
                          onChange={(e) => setDiscountVal(Math.max(0, parseFloat(e.target.value) || 0))}
                          style={{ borderRadius: '8px' }}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '11px' }}>GST Tax Rate (%)</Form.Label>
                        <Form.Select
                          size="sm"
                          value={taxRate}
                          onChange={(e) => setTaxRate(parseFloat(e.target.value) || 0)}
                          style={{ borderRadius: '8px' }}
                        >
                          <option value="0">GST 0%</option>
                          <option value="5">GST 5%</option>
                          <option value="12">GST 12%</option>
                          <option value="18">GST 18% (Standard Print)</option>
                          <option value="28">GST 28%</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '11px' }}>Shipping Charges (₹)</Form.Label>
                        <Form.Control
                          type="number"
                          size="sm"
                          min={0}
                          value={shipping}
                          onChange={(e) => setShipping(Math.max(0, parseFloat(e.target.value) || 0))}
                          style={{ borderRadius: '8px' }}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              </Col>

              <Col md={6}>
                <div className="bg-light p-3 rounded-4 border h-100 d-flex flex-column justify-content-between">
                  <div>
                    <h6 className="fw-bold text-dark mb-3"><i className="fas fa-calculator text-primary me-2" />6. Financials Summary</h6>
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted" style={{ fontSize: '13px' }}>Subtotal:</span>
                      <span className="fw-bold text-dark font-monospace" style={{ fontSize: '13px' }}>₹{subtotal.toLocaleString('en-IN')}</span>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted" style={{ fontSize: '13px' }}>Discount:</span>
                      <span className="fw-bold text-danger font-monospace" style={{ fontSize: '13px' }}>- ₹{discountAmount.toLocaleString('en-IN')}</span>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted" style={{ fontSize: '13px' }}>GST Tax:</span>
                      <span className="fw-bold text-dark font-monospace" style={{ fontSize: '13px' }}>+ ₹{taxAmount.toLocaleString('en-IN')}</span>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted" style={{ fontSize: '13px' }}>Shipping Charges:</span>
                      <span className="fw-bold text-dark font-monospace" style={{ fontSize: '13px' }}>+ ₹{shipping.toLocaleString('en-IN')}</span>
                    </div>
                    <hr className="my-2" />
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <span className="fw-bold text-dark fs-6">Grand Total:</span>
                      <span className="fw-extrabold text-primary font-monospace fs-4">₹{grandTotal.toLocaleString('en-IN')}</span>
                    </div>
                  </div>

                  <div className="d-flex flex-column gap-2">
                    <Button
                      variant="success"
                      className="fw-bold w-100 py-2 d-flex align-items-center justify-content-center gap-2"
                      style={{ borderRadius: '10px' }}
                      onClick={handleSaveInvoice}
                      disabled={addedItems.length === 0}
                    >
                      <i className="fas fa-save" /> Save Invoice & Download
                    </Button>
                    <div className="d-flex gap-2">
                      <Button
                        variant="outline-primary"
                        className="fw-bold flex-grow-1 py-2 d-flex align-items-center justify-content-center gap-1.5"
                        style={{ borderRadius: '8px', fontSize: '13px' }}
                        onClick={() => handleDownloadPDF({
                          invoiceNo,
                          date: invoiceDate,
                          dueDate,
                          preparedBy,
                          paymentStatus,
                          paymentMode,
                          clientInfo: {
                            name: clientName || 'Walk-in Customer',
                            company: clientCompany,
                            phone: clientPhone,
                            email: clientEmail,
                            address: clientAddress,
                            gstin: clientGstin,
                          },
                          items: addedItems,
                          subtotal,
                          discountType,
                          discountVal,
                          discountAmount,
                          taxRate,
                          taxAmount,
                          shipping,
                          grandTotal,
                          savedAt: new Date().toISOString(),
                        })}
                        disabled={addedItems.length === 0}
                      >
                        <i className="fas fa-file-pdf" /> Export PDF
                      </Button>
                      <Button
                        variant="outline-success"
                        className="fw-bold flex-grow-1 py-2 d-flex align-items-center justify-content-center gap-1.5"
                        style={{ borderRadius: '8px', fontSize: '13px' }}
                        onClick={() => handleExportExcel({
                          invoiceNo,
                          date: invoiceDate,
                          dueDate,
                          preparedBy,
                          paymentStatus,
                          paymentMode,
                          clientInfo: {
                            name: clientName || 'Walk-in Customer',
                            company: clientCompany,
                            phone: clientPhone,
                            email: clientEmail,
                            address: clientAddress,
                            gstin: clientGstin,
                          },
                          items: addedItems,
                          subtotal,
                          discountType,
                          discountVal,
                          discountAmount,
                          taxRate,
                          taxAmount,
                          shipping,
                          grandTotal,
                          savedAt: new Date().toISOString(),
                        })}
                        disabled={addedItems.length === 0}
                      >
                        <i className="fas fa-file-excel" /> Export Excel
                      </Button>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        ) : (
          /* Invoice History Tab */
          <div>
            <div className="d-flex flex-wrap gap-3 justify-content-between align-items-end mb-4 bg-light p-3 rounded-4 border">
              <div className="d-flex gap-3 flex-grow-1 flex-wrap">
                <Form.Group style={{ minWidth: '250px' }}>
                  <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '11px' }}>Search Invoice</Form.Label>
                  <div className="input-group input-group-sm">
                    <span className="input-group-text bg-white border-end-0">
                      <i className="fas fa-search text-muted"></i>
                    </span>
                    <Form.Control
                      type="text"
                      placeholder="Name, company, phone, invoice no..."
                      value={customerSearch}
                      onChange={(e) => setCustomerSearch(e.target.value)}
                      className="border-start-0 ps-0"
                    />
                  </div>
                </Form.Group>
                <Form.Group style={{ minWidth: '150px' }}>
                  <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '11px' }}>Sort By</Form.Label>
                  <Form.Select
                    size="sm"
                    value={sortOption}
                    onChange={(e) => setSortOption(e.target.value as any)}
                    style={{ borderRadius: '8px' }}
                  >
                    <option value="newest">Newest First</option>
                    <option value="oldest">Oldest First</option>
                    <option value="highest">Highest Value</option>
                    <option value="lowest">Lowest Value</option>
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="d-flex gap-2 align-items-end flex-wrap">
                <span className="fw-semibold text-muted me-2 mb-1" style={{ fontSize: '11px' }}>Bulk Export:</span>
                <Button variant="outline-success" size="sm" onClick={() => handleBulkExport('day')} style={{ borderRadius: '8px' }}>
                  <i className="fas fa-file-excel me-1" /> Today
                </Button>
                <Button variant="outline-success" size="sm" onClick={() => handleBulkExport('week')} style={{ borderRadius: '8px' }}>
                  <i className="fas fa-file-excel me-1" /> This Week
                </Button>
                <Button variant="outline-success" size="sm" onClick={() => handleBulkExport('month')} style={{ borderRadius: '8px' }}>
                  <i className="fas fa-file-excel me-1" /> This Month
                </Button>
                <Button variant="outline-success" size="sm" onClick={() => handleBulkExport('year')} style={{ borderRadius: '8px' }}>
                  <i className="fas fa-file-excel me-1" /> This Year
                </Button>
                {/* Date-range calendar export */}
                <div className="position-relative">
                  <Button
                    variant={showDatePicker ? 'primary' : 'outline-primary'}
                    size="sm"
                    onClick={() => setShowDatePicker(v => !v)}
                    style={{ borderRadius: '8px' }}
                    title="Export by date range"
                  >
                    <i className="fas fa-calendar-alt me-1" /> By Date
                  </Button>
                  {showDatePicker && (
                    <div
                      style={{
                        position: 'absolute',
                        right: 0,
                        top: '110%',
                        zIndex: 9999,
                        background: '#fff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '14px',
                        boxShadow: '0 8px 32px rgba(34,43,69,0.18)',
                        padding: '20px 22px 16px',
                        minWidth: '310px',
                      }}
                    >
                      {/* Header */}
                      <div className="d-flex align-items-center justify-content-between mb-3">
                        <div className="d-flex align-items-center gap-2">
                          <span
                            style={{
                              background: 'linear-gradient(135deg,#3b82f6,#6366f1)',
                              borderRadius: '8px',
                              padding: '5px 8px',
                              display: 'inline-flex',
                            }}
                          >
                            <i className="fas fa-calendar-alt text-white" style={{ fontSize: '13px' }} />
                          </span>
                          <span className="fw-bold text-dark" style={{ fontSize: '13px' }}>Date-wise Export</span>
                        </div>
                        <button
                          type="button"
                          className="btn btn-sm btn-light border-0"
                          style={{ borderRadius: '8px', lineHeight: 1, padding: '3px 8px' }}
                          onClick={() => setShowDatePicker(false)}
                        >
                          <i className="fas fa-times" />
                        </button>
                      </div>

                      {/* Date inputs */}
                      <div className="d-flex flex-column gap-2 mb-3">
                        <div>
                          <label className="fw-semibold text-muted mb-1 d-block" style={{ fontSize: '11px' }}>
                            <i className="fas fa-play-circle me-1 text-success" style={{ fontSize: '10px' }} />From Date
                          </label>
                          <Form.Control
                            type="date"
                            size="sm"
                            value={dateRangeStart}
                            onChange={e => setDateRangeStart(e.target.value)}
                            style={{ borderRadius: '8px', fontSize: '13px' }}
                          />
                        </div>
                        <div>
                          <label className="fw-semibold text-muted mb-1 d-block" style={{ fontSize: '11px' }}>
                            <i className="fas fa-stop-circle me-1 text-danger" style={{ fontSize: '10px' }} />To Date
                          </label>
                          <Form.Control
                            type="date"
                            size="sm"
                            value={dateRangeEnd}
                            onChange={e => setDateRangeEnd(e.target.value)}
                            min={dateRangeStart || undefined}
                            style={{ borderRadius: '8px', fontSize: '13px' }}
                          />
                        </div>
                      </div>

                      {/* Preview count */}
                      {dateRangeStart && dateRangeEnd && (() => {
                        const s = new Date(dateRangeStart);
                        const e = new Date(dateRangeEnd);
                        e.setHours(23, 59, 59, 999);
                        const count = invoices.filter(inv => {
                          if (!inv.date) return false;
                          const d = new Date(inv.date);
                          return d >= s && d <= e;
                        }).length;
                        return (
                          <div
                            className="text-center mb-3 py-2 rounded-3"
                            style={{
                              background: count > 0 ? '#f0fdf4' : '#fef9c3',
                              border: count > 0 ? '1px solid #bbf7d0' : '1px solid #fde68a',
                              fontSize: '12px',
                            }}
                          >
                            <i
                              className={`fas ${count > 0 ? 'fa-check-circle text-success' : 'fa-exclamation-circle text-warning'} me-1`}
                            />
                            <strong>{count}</strong> invoice{count !== 1 ? 's' : ''} found in range
                          </div>
                        );
                      })()}

                      {/* Quick presets */}
                      <div className="mb-3">
                        <div className="fw-semibold text-muted mb-1" style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Quick Presets</div>
                        <div className="d-flex flex-wrap gap-1">
                          {([
                            { label: 'Today', getDates: () => { const t = new Date().toISOString().split('T')[0]; return [t, t]; } },
                            { label: 'Yesterday', getDates: () => { const y = new Date(); y.setDate(y.getDate() - 1); const d = y.toISOString().split('T')[0]; return [d, d]; } },
                            { label: 'Last 7d', getDates: () => { const now = new Date(); const w = new Date(now.getTime() - 6*24*60*60*1000); return [w.toISOString().split('T')[0], now.toISOString().split('T')[0]]; } },
                            { label: 'Last 30d', getDates: () => { const now = new Date(); const m = new Date(now.getTime() - 29*24*60*60*1000); return [m.toISOString().split('T')[0], now.toISOString().split('T')[0]]; } },
                            { label: 'This Month', getDates: () => { const n = new Date(); const s = new Date(n.getFullYear(), n.getMonth(), 1).toISOString().split('T')[0]; return [s, n.toISOString().split('T')[0]]; } },
                            { label: 'Last Month', getDates: () => { const n = new Date(); const s = new Date(n.getFullYear(), n.getMonth()-1, 1); const e2 = new Date(n.getFullYear(), n.getMonth(), 0); return [s.toISOString().split('T')[0], e2.toISOString().split('T')[0]]; } },
                          ] as { label: string; getDates: () => [string, string] }[]).map(preset => (
                            <button
                              key={preset.label}
                              type="button"
                              className="btn btn-outline-secondary btn-sm"
                              style={{ borderRadius: '6px', fontSize: '11px', padding: '2px 8px' }}
                              onClick={() => { const [s, e2] = preset.getDates(); setDateRangeStart(s); setDateRangeEnd(e2); }}
                            >
                              {preset.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Export buttons */}
                      <div className="d-flex gap-2">
                        <Button
                          variant="success"
                          size="sm"
                          className="flex-fill fw-bold d-flex align-items-center justify-content-center gap-2"
                          style={{ borderRadius: '8px' }}
                          onClick={handleDateRangeExport}
                          disabled={!dateRangeStart || !dateRangeEnd}
                        >
                          <i className="fas fa-file-excel" />
                          Excel
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          className="flex-fill fw-bold d-flex align-items-center justify-content-center gap-2"
                          style={{ borderRadius: '8px' }}
                          onClick={handleDateRangePDFExport}
                          disabled={!dateRangeStart || !dateRangeEnd}
                        >
                          <i className="fas fa-file-pdf" />
                          PDF
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {filteredAndSortedInvoices.length === 0 ? (
              <Alert variant="info" className="text-center rounded-4 py-5 border-0">
                <i className="fas fa-history mb-2" style={{ fontSize: '28px' }} />
                <h5 className="fw-semibold text-dark">No Invoices Found</h5>
                <p className="text-muted mb-0" style={{ fontSize: '13px' }}>Try adjusting your search or filters.</p>
              </Alert>
            ) : (
              <div className="table-responsive bg-white rounded-3 border">
                <Table hover className="align-middle mb-0" style={{ fontSize: '13px' }}>
                  <thead className="table-light">
                    <tr>
                      <th className="py-2.5 px-3">Invoice No</th>
                      <th className="py-2.5">Customer</th>
                      <th className="py-2.5">Date / Due Date</th>
                      <th className="py-2.5 text-center">Payment Status</th>
                      <th className="py-2.5 text-center">Order Status</th>
                      <th className="py-2.5 text-center">Payment Mode</th>
                      <th className="py-2.5 text-end">Grand Total</th>
                      <th className="py-2.5">Prepared By</th>
                      <th className="py-2.5 text-end px-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAndSortedInvoices.map((inv) => (
                      <tr key={inv.invoiceNo}>
                        <td className="py-2 px-3 fw-bold text-primary font-monospace">{inv.invoiceNo}</td>
                        <td>
                          <div className="fw-bold text-dark">{inv.clientInfo.name}</div>
                          {inv.clientInfo.company && <div className="text-muted" style={{ fontSize: '11px' }}>{inv.clientInfo.company}</div>}
                        </td>
                        <td>
                          <div className="text-dark">Date: {inv.date}</div>
                          <div className="text-danger" style={{ fontSize: '11px' }}>Due: {inv.dueDate}</div>
                        </td>
                        <td className="text-center">
                          <Form.Select
                            size="sm"
                            value={inv.paymentStatus || 'Pending'}
                            onChange={(e) => handleUpdateInvoicePaymentStatus(inv.invoiceNo, e.target.value)}
                            style={{ borderRadius: '8px', fontSize: '11.5px', minWidth: '100px' }}
                          >
                            <option value="Pending">Pending</option>
                            <option value="Partially Paid">Partially Paid</option>
                            <option value="Paid">Paid</option>
                          </Form.Select>
                        </td>
                        <td className="text-center">
                          {inv.orderId ? (
                            <Form.Select
                              size="sm"
                              value={inv.orderStatus || 'pending'}
                              onChange={(e) => handleUpdateOrderStatus(inv.orderId!, e.target.value)}
                              style={{ borderRadius: '8px', fontSize: '11.5px', minWidth: '100px' }}
                            >
                              <option value="pending">Pending</option>
                              <option value="paid">Paid</option>
                              <option value="confirmed">Confirmed</option>
                              <option value="in_progress">In Progress</option>
                              <option value="completed">Completed</option>
                              <option value="cancelled">Cancelled</option>
                            </Form.Select>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td className="text-center font-monospace" style={{ fontSize: '11.5px' }}>{inv.paymentMode}</td>
                        <td className="text-end fw-bold text-dark">₹{(inv.totalAmount || inv.grandTotal || 0).toLocaleString('en-IN')}</td>
                        <td>
                          <span className="text-dark">{inv.preparedBy}</span>
                        </td>
                        <td className="text-end px-3">
                          <div className="d-flex align-items-center justify-content-end gap-1.5">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleDownloadPDF(inv)}
                              title="Download PDF"
                            >
                              <i className="fas fa-file-pdf" />
                            </Button>
                            <Button
                              variant="outline-success"
                              size="sm"
                              onClick={() => handleExportExcel(inv)}
                              title="Export Excel"
                            >
                              <i className="fas fa-file-excel" />
                            </Button>
                            <Button
                              variant="outline-warning"
                              size="sm"
                              onClick={() => handleEditInvoice(inv)}
                              title="Edit invoice"
                            >
                              <i className="fas fa-edit" />
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDeleteInvoice(inv.invoiceNo)}
                              title="Delete from history"
                            >
                              <i className="fas fa-trash" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </div>
        )}
      </Card.Body>
      <CustomerModal
        show={showAddCustomerModal}
        onHide={() => setShowAddCustomerModal(false)}
        onCustomerSaved={(savedCustomer) => {
          setSelectedCustomerIdState(savedCustomer.id);
          setClientName(savedCustomer.username || '');
          setClientCompany(savedCustomer.companyName || '');
          setClientPhone(savedCustomer.phone || '');
          setClientEmail(savedCustomer.email || '');
          setClientAddress(savedCustomer.companyAddress || savedCustomer.shippingAddress || '');
          setClientGstin(savedCustomer.gstNumber || '');
        }}
      />
    </Card>
  );
};

export default InvoiceGenerator;
